import React from 'react';
import { TableVirtuoso, TableComponents } from 'react-virtuoso';
import { Lead } from '../../../entities/lead/model/types';
import { BoardStage } from '../../../entities/board';
import { formatDate, getManagerInitials, getManagerColor } from '../../../shared/lib/utils';
import { getThemeFromStage } from '../../../shared/config/stages';

interface LeadsTableProps {
  leads: Lead[];
  stages: BoardStage[];
  isLoading: boolean;
  hasMore?: boolean;
  onRowClick: (id: string, title?: string) => void;
  onLoadMore?: () => void;
}

type Ctx = {
  leads: Lead[];
  stageMap: Record<string, BoardStage>;
  onRowClick: (id: string, title?: string) => void;
  isLoading: boolean;
};

// Все компоненты вынесены на уровень модуля — их референсы стабильны.
// Если они inline в JSX, TableVirtuoso ремаунтит Scroller при каждом ре-рендере
// и скролл сбрасывается в начало.
const TABLE_COMPONENTS: TableComponents<Lead, Ctx> = {
  Scroller: React.forwardRef((props, ref) => (
    <div ref={ref} {...props} className="table-scroll" />
  )),
  Table: ({ style, ...props }) => <table className="leads-table" style={style} {...props} />,
  TableHead: React.forwardRef((props, ref) => <thead ref={ref} {...props} />),
  TableBody: React.forwardRef((props, ref) => <tbody ref={ref} {...props} />),
  TableRow: ({ context, 'data-item-index': idx, ...props }: any) => {
    const lead: Lead | undefined = context?.leads[idx];
    const hasNoJobs = (lead?.statusJobs?.open ?? 0) === 0 || (lead?.statusJobs?.exp ?? 0) > 0;
    return (
      <tr
        className={`lt-row${hasNoJobs ? ' lt-row-no-jobs' : ''}`}
        onClick={() => lead && context?.onRowClick(lead.id, lead.title)}
        {...props}
      />
    );
  },
  EmptyPlaceholder: () => (
    <tbody>
      <tr><td colSpan={9} className="lt-empty">Нет записей</td></tr>
    </tbody>
  ),
  Footer: ({ context }: { context?: Ctx }) => context?.isLoading ? (
    <div className="col-spinner" style={{ padding: '12px 0' }}>Загрузка...</div>
  ) : null,
};

const HEADER = () => (
  <tr>
    <th>ID</th>
    <th>Название</th>
    <th>Стадия</th>
    <th>Менеджер</th>
    <th>Город выезда</th>
    <th>Город назначения</th>
    <th>Отправка</th>
    <th>Приезд</th>
    <th>Создан</th>
  </tr>
);

const LeadsTable: React.FC<LeadsTableProps> = ({ leads, stages, isLoading, hasMore, onRowClick, onLoadMore }) => {
  const stageMap = Object.fromEntries(stages.map(s => [s.id, s]));

  if (isLoading && leads.length === 0) {
    return (
      <div className="table-wrap" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: 'var(--text2)', fontSize: 15, fontWeight: 600 }}>Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="table-wrap">
      <TableVirtuoso<Lead, Ctx>
        style={{ flex: 1, minHeight: 0 }}
        data={leads}
        endReached={hasMore && !isLoading ? onLoadMore : undefined}
        overscan={300}
        context={{ leads, stageMap, onRowClick, isLoading }}
        components={TABLE_COMPONENTS}
        fixedHeaderContent={HEADER}
        itemContent={(_, lead) => {
          const stage = lead.boardId ? stageMap[lead.boardId] : null;
          const theme = stage ? getThemeFromStage(stage) : null;
          const managerName = lead.expand?.userId?.name ?? lead.userId ?? 'Без менеджера';
          const managerEmail = lead.expand?.userId?.email;
          const managerColorKey = lead.expand?.userId?.id ?? lead.userId ?? managerName;
          const initials = getManagerInitials(managerName, managerEmail);
          const color = getManagerColor(managerColorKey);
          const parts = managerName.trim().split(' ');
          const shortName = `${parts[0] ?? ''} ${parts[1]?.[0] ?? ''}.`.trim();

          return (
            <>
              <td className="lt-id">#{lead.id.slice(-4)}</td>
              <td className="lt-title">{lead.title?.trim() || 'Без названия'}</td>
              <td>
                {stage && theme ? (
                  <span
                    className="lt-stage-badge"
                    style={{ background: theme.background, color: theme.text, borderColor: theme.border }}
                  >
                    {stage.name}
                  </span>
                ) : '—'}
              </td>
              <td>
                <div className="lt-mgr">
                  <div className="mgr-av" style={{ background: color }}>{initials}</div>
                  <span>{shortName || managerName}</span>
                </div>
              </td>
              <td>{lead.city?.trim() || '—'}</td>
              <td>{lead.destination?.trim() || '—'}</td>
              <td>{formatDate(lead.sending ?? '') || '—'}</td>
              <td>{lead.arrival ? formatDate(lead.arrival) : '—'}</td>
              <td>{formatDate(lead.created)}</td>
            </>
          );
        }}
      />
    </div>
  );
};

export { LeadsTable };
